import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useTranslation } from 'react-i18next';
import { CompanyInfosManagement } from '../components/CompanyInfosManagement';
import { useCreateCompany } from '@/hooks/company.hooks';
import {
  companySchema,
  TCompanySchema,
} from '@/schemas/company/company.schema';
import Loader from '@/components/Loading/Loader';
import { getLanguage } from '@/utils/language.utils';

export const CreateCompanyPage = () => {
  const { t } = useTranslation('company');
  const navigate = useNavigate();
  const {
    watch: watchCompany,
    control: companyControl,
    register: registerCompany,
    setValue: setCompanyValue,
    setError: setCompanyError,
    handleSubmit: handleSubmitCompany,
    formState: { errors: companyErrors, isSubmitting: isSubmittingCompany },
  } = useForm<TCompanySchema>({
    resolver: zodResolver(companySchema),
  });

  const { mutate: mutateCreateCompany, isPending: isPendingCreateCompany } =
    useCreateCompany({
      t,
      handleSuccess: () => navigate('/companies'),
    });

  const onSubmitCompany = (company: TCompanySchema) => {
    const { languageCode } = getLanguage();

    const newCompany = {
      ...company,
      languageCode,
    };

    mutateCreateCompany(newCompany);
  };

  return (
    <>
      <PageHeader
        backToLink="/companies"
        title={t('common:company')}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('companyList')} linkTo="/companies" />
            <BreadcrumbItem text={t('common:company')} active />
          </BreadcrumbList>
        }
      />
      <div className="flex flex-col gap-12">
        <CompanyInfosManagement<TCompanySchema>
          t={t}
          watch={watchCompany}
          control={companyControl}
          register={registerCompany}
          setValue={setCompanyValue}
          setError={setCompanyError}
          errors={companyErrors}
          submitDisabled={isSubmittingCompany || isPendingCreateCompany}
          onSubmit={handleSubmitCompany(onSubmitCompany)}
        />
      </div>
      {(isSubmittingCompany || isPendingCreateCompany) && <Loader />}
    </>
  );
};
